import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {ListResult, SingleResult} from 'utils/types';

/**
 * Saved RFP Feasibility selections, and the one of them a client sees.
 *
 * Two shapes for one row. Staff read `SavedRfpFeasibility` off `/rfp_feasibility/saved/`;
 * clients read `PublishedRfpFeasibility` off `/rfp_feasibility/published/`, which the API
 * narrows to the `summary_` fields and a direction rather than an iROAS figure.
 */

export type IroasDirection = 'positive' | 'negative';

/**
 * One product in a published selection, resolved from the data when the selection was saved.
 *
 * `item_identifier` is a UPC for RFP files. Description and brand are `''` when the file's adapter
 * carried no such column, which is ordinary rather than an error.
 */
export interface PublishedRfpItem {
  item_identifier: string;
  item_description: string;
  brand: string;
}

/** What the client is shown: the shape of the recommended build, never the money behind it. */
export interface PublishedRfpFeasibility {
  id: number;
  summary_period_type: string;
  summary_week_ending: string | null;
  summary_retailers: string[];
  summary_regions: string[];
  /** Every market in scope, including any past the cap that `summary_regions` stops at. */
  summary_region_count: number;
  summary_items: PublishedRfpItem[];
  /** Every product in scope, including any past the cap that `summary_items` stops at. */
  summary_item_count: number;
  /** Null when the campaign has no budget, so iROAS is undefined rather than bad. */
  iroas_direction: IroasDirection | null;
  updated_at: string;
}

export interface SavedRfpFeasibility {
  id: number;
  campaign: number;
  /** Everything scoping the internal page, so it can be put back the way it was. */
  filters: Record<string, unknown>;
  item_rows: string[];
  item_group_by: string[];
  item_metrics: string[];
  item_order_by: string[];
  location_rows: string[];
  location_group_by: string[];
  location_metrics: string[];
  location_order_by: string[];
  limit_to_top_regions: number | null;
  summary_period_type: string;
  summary_week_ending: string | null;
  summary_retailers: string[];
  summary_regions: string[];
  summary_region_count: number;
  summary_items: PublishedRfpItem[];
  summary_item_count: number;
  summary_iroas: number | null;
  published: boolean;
  /** Internal only. Never sent to a client, and editable after publish. */
  notes: string;
  created_at: string;
  updated_at: string;
}

/**
 * A create body. The server owns the id and the timestamps, `published` starts false, and the
 * item list is resolved server-side from the selection's own filters rather than sent.
 */
export type SaveRfpFeasibilityPayload = Omit<
  SavedRfpFeasibility,
  | 'id'
  | 'created_at'
  | 'updated_at'
  | 'published'
  | 'summary_items'
  | 'summary_item_count'
  | 'summary_regions'
  | 'summary_region_count'
  | 'summary_retailers'
>;

export interface IState {
  savedList: ListResult<SavedRfpFeasibility>;
  published: SingleResult<PublishedRfpFeasibility | null>;
}

export const rfpFeasibilitySavedEndpoints = {
  listSaved: createAsyncThunk('rfpFeasibilitySaved/listSaved', async (campaignId: number, {rejectWithValue}) => {
    try {
      const response = await ApiService.get(`/rfp_feasibility/saved/?campaign_id=${campaignId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }),
  createSaved: createAsyncThunk(
    'rfpFeasibilitySaved/createSaved',
    async (payload: SaveRfpFeasibilityPayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.post('/rfp_feasibility/saved/', payload);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  /**
   * Publishing, unpublishing and annotating - the only edits a saved selection allows. The
   * selection itself is write-once, so the API rejects nothing else here, it simply ignores it.
   */
  updateSaved: createAsyncThunk(
    'rfpFeasibilitySaved/updateSaved',
    async (payload: {id: number; published?: boolean; notes?: string}, {rejectWithValue}) => {
      const {id, ...body} = payload;
      try {
        const response = await ApiService.patch(`/rfp_feasibility/saved/${id}/`, body);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  getPublished: createAsyncThunk('rfpFeasibilitySaved/getPublished', async (campaignId: number, {rejectWithValue}) => {
    try {
      const response = await ApiService.get(`/rfp_feasibility/published/?campaign_id=${campaignId}`);
      // Nothing published yet is an ordinary state, so the API answers `{result: null}` rather
      // than 404. Unwrapped here so the page reads `value === null` and shows its empty state.
      return {value: response.data.result as PublishedRfpFeasibility | null};
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }),
};

export const rfpFeasibilitySavedSlice = createSlice({
  name: 'rfpFeasibilitySaved',
  initialState: {
    savedList: {as: 'idle'},
    published: {as: 'idle'},
  } as IState,
  reducers: {},
  extraReducers: builder => {
    addResultCases(builder, rfpFeasibilitySavedEndpoints.listSaved, 'savedList');
    addResultCases(builder, rfpFeasibilitySavedEndpoints.getPublished, 'published');
  },
});

export const rfpFeasibilitySavedActions = rfpFeasibilitySavedSlice.actions;

export type RfpFeasibilitySavedSlice = {
  [rfpFeasibilitySavedSlice.name]: ReturnType<(typeof rfpFeasibilitySavedSlice)['reducer']>;
};
